import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import { PROGRAMS } from "@/lib/content";

function ProgramCard({ p, index }) {
  const Icon = Icons[p.icon] || Icons.Cpu;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.06 }}
      className="border-l border-[var(--ri-ink)] first:border-t-0 group"
      data-testid={`program-card-${p.slug}`}
    >
      <Link to="/programs" className="block p-6 md:p-8 hover:bg-white transition-colors h-full">
        <div className="flex items-start justify-between mb-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ri-muted)]">
            {p.code}
          </span>
          <Icon
            size={28}
            strokeWidth={1.4}
            className="text-[var(--ri-ink)] group-hover:text-[var(--ri-orange)] transition-colors"
          />
        </div>
        <h3 className="font-display text-2xl md:text-3xl leading-[1.05] tracking-tight">
          {p.title}
        </h3>
        <p className="mt-4 text-[var(--ri-muted)] text-sm leading-relaxed">
          {p.blurb}
        </p>
        <div className="mt-6 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.14em]">
          <span>{p.duration}</span>
          <span className="text-[var(--ri-orange)]">{p.level}</span>
        </div>
        <div className="mt-6 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ri-ink)] border-b border-[var(--ri-ink)] pb-1 group-hover:gap-3 transition-all">
          Explore <ArrowUpRight size={12} />
        </div>
      </Link>
    </motion.div>
  );
}

export default function ProgramsGrid({ withHeading = true }) {
  return (
    <section
      data-testid="programs-grid"
      className="border-t border-[var(--ri-ink)] bg-[var(--ri-bg)]"
    >
      {withHeading && (
        <div className="ri-container py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="overline mb-4">
                <span className="dot mr-2" /> 01 — Programs
              </div>
              <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95]">
                Five programs.<br />
                One <span className="text-[var(--ri-orange)]">future-ready</span> learner.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[var(--ri-muted)] text-base leading-relaxed">
                Each track is curriculum-aligned, outcomes-graded and built for institutions running on tight schedules and bigger ambitions. Hands-on. Mentor-led. Showcase-driven.
              </p>
              <Link
                to="/programs"
                data-testid="programs-grid-cta"
                className="btn-link mt-5 inline-flex"
              >
                See all program details →
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="ri-container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-r border-b border-[var(--ri-ink)]">
          {PROGRAMS.map((p, i) => (
            <ProgramCard key={p.slug} p={p} index={i} />
          ))}
          <div
            className="border-l border-b lg:border-b-0 border-[var(--ri-ink)] p-6 md:p-8 bg-[var(--ri-ink)] text-white flex flex-col justify-between"
            data-testid="programs-grid-cta-card"
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                PRG/00
              </div>
              <h3 className="mt-6 font-display text-3xl leading-[1.05]">
                Custom RoboInsci track for your institution
              </h3>
              <p className="mt-4 text-white/60 text-sm">
                Mix robotics, AI, IoT, app-dev, 3D-printing and mentorship into a single year-long program tailored to your school or college.
              </p>
            </div>
            <Link
              to="/contact"
              className="btn-primary mt-8 self-start"
              data-testid="programs-custom-cta"
            >
              Build my track <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      <div className="h-20" />
    </section>
  );
}
